import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Trash2, Upload, ArrowUp, ArrowDown, Save } from "lucide-react";
import { z } from "zod";
import { FileText } from "lucide-react";
import {
  uploadSponsorInfoPdf,
  getSponsorInfoPdfUrl,
} from "@/lib/sponsor-application.functions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/_authenticated/admin/sponsors")({
  head: () => ({
    meta: [
      { title: "Admin — Partners beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminSponsorsPage,
});

type SponsorRow = {
  id: string;
  company: string;
  logo_url: string | null;
  status: string;
  display_order: number;
  tier: "hoofdpartner" | "premium" | "partner" | "supporter" | "vriend";
  bg_variant: "light" | "dark" | "transparent";
  invert_on_dark: boolean;
  website_url: string | null;
};

function SponsorLogo({
  pathOrUrl,
  alt,
  bgVariant = "light",
  invert = false,
}: {
  pathOrUrl: string | null;
  alt: string;
  bgVariant?: "light" | "dark" | "transparent";
  invert?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!pathOrUrl) return;
    if (/^https?:\/\//i.test(pathOrUrl)) {
      setSrc(pathOrUrl);
      return;
    }
    supabase.storage
      .from("sponsor-logos")
      .createSignedUrl(pathOrUrl, 60 * 60)
      .then(({ data }) => setSrc(data?.signedUrl ?? null));
  }, [pathOrUrl]);
  const bg =
    bgVariant === "dark"
      ? "bg-night-deep"
      : bgVariant === "transparent"
        ? "bg-transparent"
        : "bg-cream";
  if (!src) return <div className={`h-14 w-24 rounded ${bg}`} />;
  return (
    <div className={`flex h-14 w-24 items-center justify-center rounded-lg ${bg}`}>
      <img
        src={src}
        alt={alt}
        width={80}
        height={48}
        className={`max-h-10 max-w-[80px] object-contain ${bgVariant === "dark" && invert ? "invert brightness-0" : ""}`}
      />
    </div>
  );
}

const schema = z.object({
  company: z.string().trim().min(1).max(200),
});

// Auto-crop transparent / near-white borders from a logo, then re-encode as PNG.
async function autoCropLogo(file: File): Promise<File> {
  try {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return file;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, w, h);
    // A pixel is "content" if not transparent AND not near-white.
    const isContent = (i: number) => {
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      if (a < 16) return false;
      return !(r > 240 && g > 240 && b > 240);
    };
    let top = h, bottom = -1, left = w, right = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (isContent((y * w + x) * 4)) {
          if (y < top) top = y;
          if (y > bottom) bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }
    if (bottom < 0 || right < 0) return file;
    const pad = Math.round(Math.max(right - left, bottom - top) * 0.04);
    const sx = Math.max(0, left - pad);
    const sy = Math.max(0, top - pad);
    const sw = Math.min(w, right + pad) - sx + 1;
    const sh = Math.min(h, bottom + pad) - sy + 1;
    if (sw <= 0 || sh <= 0) return file;
    const out = document.createElement("canvas");
    out.width = sw;
    out.height = sh;
    const octx = out.getContext("2d");
    if (!octx) return file;
    octx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
    const blob: Blob | null = await new Promise((resolve) => out.toBlob(resolve, "image/png"));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".png", { type: "image/png" });
  } catch {
    return file;
  }
}

function AdminSponsorsPage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [sponsors, setSponsors] = useState<SponsorRow[]>([]);
  const [company, setCompany] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [currentPdf, setCurrentPdf] = useState<{ url: string | null; fileName: string | null }>({ url: null, fileName: null });

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin");
      setIsAdmin(!!roles && roles.length > 0);
    })();
  }, []);

  async function loadSponsors() {
    const { data, error } = await supabase
      .from("sponsors")
      .select("id, company, logo_url, status, display_order, tier, bg_variant, invert_on_dark, website_url")
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (!error && data) setSponsors(data as SponsorRow[]);
  }

  useEffect(() => {
    if (isAdmin) loadSponsors();
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) getSponsorInfoPdfUrl().then(setCurrentPdf).catch(() => {});
  }, [isAdmin]);

  async function uploadPdf(e: React.FormEvent) {
    e.preventDefault();
    if (!pdfFile) return toast.error("Kies een PDF");
    if (pdfFile.type !== "application/pdf") return toast.error("Alleen PDF toegestaan");
    if (pdfFile.size > 20 * 1024 * 1024) return toast.error("PDF mag maximaal 20MB zijn");
    setPdfUploading(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.onerror = () => reject(r.error);
        r.readAsDataURL(pdfFile);
      });
      await uploadSponsorInfoPdf({ data: { name: pdfFile.name, dataBase64 } });
      toast.success("Partnermap geüpload");
      setPdfFile(null);
      const next = await getSponsorInfoPdfUrl();
      setCurrentPdf(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload mislukt");
    } finally {
      setPdfUploading(false);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  async function addSponsor(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ company });
    if (!parsed.success) return toast.error("Vul een bedrijfsnaam in");
    if (!file) return toast.error("Kies een logo bestand");
    setSaving(true);
    const cropped = await autoCropLogo(file);
    const ext = cropped.name.split(".").pop() || "png";
    const path = `logos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const up = await supabase.storage.from("sponsor-logos").upload(path, cropped, {
      contentType: cropped.type,
      upsert: false,
    });
    if (up.error) {
      setSaving(false);
      return toast.error("Upload mislukt: " + up.error.message);
    }
    // Place new sponsors at the end so they don't collide on display_order = 0.
    const maxOrder = sponsors.reduce((m, s) => Math.max(m, s.display_order ?? 0), 0);
    const { error } = await supabase.from("sponsors").insert({
      company: parsed.data.company,
      email: "admin@openluchtfilmfestival.nl",
      contact_person: "admin",
      budget: "1k_3k",
      logo_url: path,
      status: "approved",
      display_order: maxOrder + 10,
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Partner toegevoegd");
    setCompany("");
    setFile(null);
    loadSponsors();
  }

  async function deleteSponsor(id: string) {
    if (!confirm("Verwijder deze partner?")) return;
    const { error } = await supabase.from("sponsors").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Verwijderd");
    loadSponsors();
  }

  async function toggleApprove(s: SponsorRow) {
    const next = s.status === "approved" ? "pending" : "approved";
    const { error } = await supabase.from("sponsors").update({ status: next }).eq("id", s.id);
    if (error) return toast.error(error.message);
    loadSponsors();
  }

  async function updateSponsor(id: string, patch: Partial<SponsorRow>) {
    setSponsors((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const { error } = await (supabase as any).from("sponsors").update(patch).eq("id", id);
    if (error) {
      toast.error(error.message);
      loadSponsors();
    }
  }

  async function moveSponsor(index: number, direction: "up" | "down") {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sponsors.length) return;
    // Swap in-memory, then rewrite sequential orders so duplicate / zero values can't block reordering.
    const reordered = [...sponsors];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const updates = reordered.map((s, i) => ({ id: s.id, display_order: (i + 1) * 10 }));
    // Optimistic UI
    setSponsors(reordered.map((s, i) => ({ ...s, display_order: (i + 1) * 10 })));
    for (const u of updates) {
      const prev = sponsors.find((s) => s.id === u.id)?.display_order;
      if (prev === u.display_order) continue;
      const { error } = await supabase
        .from("sponsors")
        .update({ display_order: u.display_order })
        .eq("id", u.id);
      if (error) {
        toast.error(error.message);
        loadSponsors();
        return;
      }
    }
    loadSponsors();
  }

  if (isAdmin === null) {
    return <div className="flex min-h-screen items-center justify-center text-foreground/70">Laden…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl">Geen toegang</h1>
          <p className="text-foreground/70">Je account heeft geen admin rechten.</p>
          <Button onClick={handleSignOut} variant="ghostLine">Uitloggen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-12 md:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Partners beheren</h1>
        </div>

        <form onSubmit={addSponsor} className="rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <h2 className="font-display text-xl">Nieuwe partner</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="company">Bedrijfsnaam</Label>
              <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo">Logo</Label>
              <Input id="logo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
            </div>
          </div>
          <Button type="submit" variant="hero" size="lg" disabled={saving} className="mt-6">
            <Upload className="h-4 w-4" />
            {saving ? "Bezig…" : "Partner toevoegen"}
          </Button>
        </form>

        <form onSubmit={uploadPdf} className="mt-8 rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <h2 className="font-display text-xl">Partnermap (PDF)</h2>
          <p className="mt-1 text-sm text-foreground/70">
            Zichtbaar op de verborgen partner-aanmeldpagina <code className="text-gold">/partner-aanmelden</code>.
          </p>
          {currentPdf.url && (
            <a
              href={currentPdf.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-3 py-1.5 text-sm text-gold hover:bg-night-deep"
            >
              <FileText className="h-4 w-4" />
              Huidige: {currentPdf.fileName ?? "partnermap.pdf"}
            </a>
          )}
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="partner-pdf">Nieuwe PDF</Label>
              <Input id="partner-pdf" type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)} />
            </div>
          </div>
          <Button type="submit" variant="hero" size="lg" disabled={pdfUploading} className="mt-6">
            <Upload className="h-4 w-4" />
            {pdfUploading ? "Uploaden…" : "PDF uploaden"}
          </Button>
        </form>

        <h2 className="mt-12 font-display text-xl">Partners ({sponsors.length})</h2>
        <div className="mt-4 space-y-3">
          {sponsors.map((s, i) => (
            <div key={s.id} className="rounded-xl border border-border bg-night-deep/40 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <SponsorLogo pathOrUrl={s.logo_url} alt={s.company} bgVariant={s.bg_variant} invert={s.invert_on_dark} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{s.company}</p>
                  <p className="text-xs text-foreground/60">{s.status} · volgorde {s.display_order}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghostLine" size="sm" disabled={i === 0} onClick={() => moveSponsor(i, "up")} title="Omhoog">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghostLine" size="sm" disabled={i === sponsors.length - 1} onClick={() => moveSponsor(i, "down")} title="Omlaag">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
                <Button variant="ghostLine" size="sm" onClick={() => toggleApprove(s)}>
                  {s.status === "approved" ? "Verberg" : "Publiceer"}
                </Button>
                <Button variant="ghostLine" size="sm" onClick={() => deleteSponsor(s.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-foreground/60">Tier</Label>
                  <Select value={s.tier} onValueChange={(v) => updateSponsor(s.id, { tier: v as SponsorRow["tier"] })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hoofdpartner">Hoofdpartner</SelectItem>
                      <SelectItem value="premium">Premium Partner</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="supporter">Supporter</SelectItem>
                      <SelectItem value="vriend">Vriend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-foreground/60">Kaartachtergrond</Label>
                  <Select value={s.bg_variant} onValueChange={(v) => updateSponsor(s.id, { bg_variant: v as SponsorRow["bg_variant"] })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Licht (crème)</SelectItem>
                      <SelectItem value="dark">Donker</SelectItem>
                      <SelectItem value="transparent">Transparant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-end gap-2 pb-1 text-sm">
                  <Checkbox
                    checked={s.invert_on_dark}
                    onCheckedChange={(v) => updateSponsor(s.id, { invert_on_dark: v === true })}
                  />
                  <span>Logo omkeren op donkere kaart</span>
                </label>
              </div>
              <div className="mt-3 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-foreground/60">Website (optioneel)</Label>
                <Input
                  value={s.website_url ?? ""}
                  onChange={(e) => updateSponsor(s.id, { website_url: e.target.value.trim() || null })}
                  placeholder="https://www.voorbeeld.nl"
                  type="url"
                />
              </div>
            </div>
          ))}
          {sponsors.length === 0 && <p className="text-foreground/60">Nog geen partners.</p>}
        </div>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}